import {useEffect, useLayoutEffect, useState} from "react";
import {toast} from "react-toastify";
import {CreateSupplierEmployee} from "../../../../../reduxToolkit/Providers/providersApi";
import {getPhotoPicker} from '../../../../../components/PhotoPicker/SubComponent/Models/Models';
import {errorMsg, validateMinLengthTrimmed} from '../../../../../Apis/functions';
import {
    EXTRA_DATA,
    SUPPLIER_EMPLOYEE_DATA,
    TABS_SUPPLIER_EMPLOYEE
} from '../../../../Dashboard/Company/Employees/Model/Models';
import { useSelector } from "react-redux";
import { GetUserRestriction } from "../../../../../reduxToolkit/restrictions/RestrictionApi";
import HandleChange from './../../../../../Helpers/HandleChange';
/**
 * @file SupplierFlowEmployeeCreateApp.jsx
 * @description Custom hook for managing the creation flow of a supplier employee user.
 *
 * @Email: azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @JobTitle: Full Stack Developer
 */

/**
 * Custom hook to handle the form logic and data flow for creating a supplier employee.
 *
 * @param {function} t - Translation function from i18next.
 * @param {function} dispatch - Redux dispatch function.
 * @param {function} navigate - Function to navigate programmatically.
 * @param {function} setEmptyFields - Setter for emptyFields.
 * @param {boolean} isValidEmail - Flag for valid email input.
 * @param {boolean} isValidPhoneNumber - Flag for valid phone number input.
 * @param {boolean} isValidDob - Flag for valid date of birth input.
 *
 * @returns {Object} Hook utilities and state for managing supplier employee creation.
 */
export const SupplierFlowEmployeeCreateApp = (
    {
        t,
        permission,
        dispatch,
        setEmptyFields,
        isValidEmail,
        isValidPhoneNumber,
        isValidDob,
        supplierId,
        photoPicker,
        setSecretModal,
        setSecretKey,
        setGeneratedPassword,
        passwordGenerator,
        validateEmail,
        validatePhone,
        validatePassword,
        isValidPassword,
        isValidDocumentId,
        extraData,
        extraDataEnabled,
        setExtraData,
        setExtraDataEnabled,
        headersList
    }
) => {
    const [user, setUser] = useState({...SUPPLIER_EMPLOYEE_DATA, supplierId : supplierId });

    const [steps, setSteps] = useState([]);
    const [activeStep, setActiveStep] = useState(TABS_SUPPLIER_EMPLOYEE.DETAILS.INDEX);

     const  STEP_EMPLOYEE_CREATE = (extraDataEnabled) => {
        if (extraDataEnabled){
            return [t('details'), t('other_details'), t('add_photo'),t('summary')];
        }
        return [t('details'), t('add_photo'),t('summary')];
    }
    
    useEffect(() => {
        dispatch(GetUserRestriction()).then(
            ({
                payload: {
                    data: { data, success },
                },
            }) => {
                if (success === true) {
                    setExtraDataEnabled(data?.extraDataSupplier);
                }
            },
        );
    }, []);
    

    useLayoutEffect(() => {
        setSteps(STEP_EMPLOYEE_CREATE(extraDataEnabled))
    }, [extraDataEnabled, t, permission]);

    /**
     * Resets the form fields to their initial state.
     */
    const resetForm = () => {
        setUser({...SUPPLIER_EMPLOYEE_DATA, supplierId : supplierId });
    };

    /**
     * Validates the form fields and triggers the supplier creation if valid.
     * Shows a warning toast if required fields are missing or invalid.
     */
    const FormHandler = () => {
        const hasInvalidField = !isValidDob || !isValidPhoneNumber || !isValidEmail || !isValidPassword;
        if (!user.isValidEmployee() || hasInvalidField) {
            setEmptyFields(true);
            return;
        }
        setEmptyFields(false);
        createSupplier();
    };

    const handleValidateSteps = (step) => {
        switch (step) {
            case TABS_SUPPLIER_EMPLOYEE.DETAILS.INDEX: {
                const hasInvalidField = !isValidDob || !isValidDocumentId ||  !validateEmail(user.email) || !validatePhone(user.phoneNumber) || !validatePassword(user.password);
                if (!user.isValidEmployee() || hasInvalidField) {
                    setEmptyFields(true);
                    return false;
                }
                return true;
            }
            default:
                return true;
        }
    }


    /**
     * Dispatches the action to create a supplier employee using form data and image file.
     * Displays success or error feedback using toast notifications.
     */
    const createSupplier = () => {
        let imageFile = getPhotoPicker(photoPicker);
        const password = user?.password?.trim() !== "" ? user?.password: passwordGenerator()
        dispatch(CreateSupplierEmployee({ data: user.toRegister(password, extraData), file: imageFile }))
            .then(({payload:{data:{data, success, code}}}) => {
                if (success) {
                    toast.success(t('has_been_created_successfully'))
                    setEmptyFields(false);
                    setSecretModal(true);
                    setSecretKey(data?.secret);
                    setGeneratedPassword(user?.password?.trim() !== "" ? null: password);
                }else{
                    errorMsg(code)
                    setEmptyFields(true);
                }
            },
        );
    }

    const handleStepChange = (step) => {
        setActiveStep(step);
    };
    
     /**
     * This function handles changes in the form fields for extra data. It updates the 'extraData' state by spreading the previous data
     * and updating the specific field (identified by 'name') with the new value from the form input.
     * @param e
     */
    const { handleChange } = HandleChange({
    model: extraData,
    setModel: setExtraData,
    });

    /**
         * handle for reset extra data
         */
    const handleClearExtraData = () => {
        setExtraData({...EXTRA_DATA});
    }


    return {
        user,
        setUser,
        FormHandler,
        resetForm,
        steps,
        activeStep,
        setActiveStep,
        handleStepChange,
        handleValidateSteps,
        handleChange,
        setExtraData,
        headersList,
        handleClearExtraData,
        extraData,
        extraDataEnabled
    };
}
